/**
 * useAchievementsQuery — TanStack Query hook over the Plan 02-07 achievements API.
 *
 * Endpoint:
 *   GET /api/achievements/:memberId →
 *     { memberId,
 *       achievements: AchievementView[]   // 12 entries always
 *     }
 *
 * Each entry:
 *   { key, title_key, icon_key, secret, unlocked, unlocked_at }
 *
 * Server-side privacy mask (T-02-27): for cross-member viewers, secret
 * achievements not yet unlocked have title_key/icon_key blanked. The UI
 * already handles this via AchievementCell's locked-secret branch.
 *
 * Cadence: staleTime 60s. Plan 02-15 celebrations will invalidate this
 * key after the WS `achievement.unlocked` event so the gallery ticks
 * the silhouette → unlocked tile in lockstep with the L4 takeover.
 *
 * Following Phase 2 convention (Plan 02-09 / 02-13): direct globalThis.fetch.
 */
import { useQuery } from '@tanstack/react-query';

export interface AchievementView {
  key: string;
  /** Server may blank for cross-member secret-locked entries (T-02-27). */
  title_key: string | null;
  /** Server may blank for cross-member secret-locked entries. */
  icon_key: string | null;
  secret: boolean;
  unlocked: boolean;
  unlocked_at: string | null;
}

export interface AchievementsResponse {
  memberId: string;
  achievements: AchievementView[];
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const e = new Error(`fetch_failed:${r.status}`) as Error & { status?: number };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export function useAchievements(memberId: string, enabled = true) {
  return useQuery({
    queryKey: ['achievements', memberId],
    queryFn: async () => {
      return await fetchJson<AchievementsResponse>(`/api/achievements/${memberId}`);
    },
    enabled: enabled && memberId.length > 0,
    staleTime: 60_000,
  });
}
